import * as lodash from 'lodash';

import {
  CreateSignalOptions,
  Signal,
  WritableSignal,
  signal,
} from '@angular/core';
import { SignalGetter } from '@angular/core/primitives/signals';

export type ArraySignal<T> = Signal<T[]> &
  WritableSignal<T[]> & {
    /** Updates properties on an object */
    push(value: T): number;
    executeQueue(): void;
    pushToQueue(value: T, queueTime?: number): void;
  };

export function arraySignal<T>(
  initialValue: T[],
  options?: CreateSignalOptions<T[]>
): ArraySignal<T> {
  const internal = signal<T[]>(initialValue, options) as SignalGetter<T[]> &
    WritableSignal<T[]>;

  let queue = 0;
  let internalArray: T[] = [];

  const _set = internal.set;
  const _update = internal.update;

  const _executeQueue = () => {
    _update(x => lodash.concat(x, internalArray));

    internalArray = [];
  };

  return Object.assign(internal, {
    set: (value: T[]) => {
      internalArray = [];
      _set(value);
    },

    update: (updateFn: (value: T[]) => T[]) => {
      internalArray = [];
      _update(updateFn);
    },

    push: (value: T) => {
      let index = 0;

      _update(x => {
        index = x.push(value);
        return lodash.clone(x);
      });

      return index;
    },

    /**
     * If you are using the queue, this method updates the signal's value.
     */
    executeQueue: _executeQueue,

    /**
     * Same as push, but with a queue system that minimizes multiple signal updates,
     * which also minimizes render calls if the template depends on the signal.
     * @param value Value to push
     * @param autoExecuteInMillis After how many millis should we execute the push?
     * If value is undefined or 0 value is not pushed.
     */
    pushToQueue: (value: T, autoExecuteInMillis = 0) => {
      internalArray.push(value);

      if (autoExecuteInMillis > 0) {
        queue++;

        setTimeout(() => {
          queue--;

          if (queue === 0) {
            _executeQueue();
          }
        }, autoExecuteInMillis);
      }
    },
  });
}
